import React, { useState, useEffect, useRef, Fragment } from "react";
import { Line } from "react-chartjs-2";
import { Box, Grid, Paper, Slide } from '@material-ui/core';
import MUIDataTable from "mui-datatables";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend, Filler
} from 'chart.js';
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import RequestAccessToken from '../../util/authService';
import BarChartIcon from '@material-ui/icons/BarChart';
import { refMDLoaded, practiceCoordinatorLoaded, shouldTryRefMDProfile, shouldTryCareCoordinatorProfile } from '../common/ProfileHelper'

type AcutePatientsByMonthProps = APIStore.HNIContextState &
    typeof APIStore.actionCreators;

const styles = gridtheme => ({
    root: {
        flexGrow: 1,
    },
    paper: {
        padding: theme.spacing.unit * 2,
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
});

const AcutePatientsByMonth = (props: AcutePatientsByMonthProps) => {

    let {
        PhysicianAdmitsStats,
        requestVitalsMdProfile,
        userPracticeCoordinatorProfile,
        userRefMdProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestAdmitStatsForPractice,
        requestAdmitStatsForProvider,
        requestAdmitStatsForPracticeByTag,
        receiveTokenResponse } = props;

    let [tokenState, setTokenState] = useState<APIStore.TokenType>();
    let PhysicianAdmits = {};

    if (PhysicianAdmitsStats) {
        PhysicianAdmits = PhysicianAdmitsStats;
    }


    let tokenStatePr = RequestAccessToken()
        .then((token) => {
            if (token && !tokenState) {
                setTokenState(token);
                receiveTokenResponse(token);
            }
        });

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }

    }, [APIToken, tokenState])

    useEffect(() => {
        if (shouldTryRefMDProfile(userRefMdProfile) && tokenState?.accessToken && userPracticeCoordinatorProfile.notFound) {
            requestVitalsMdProfile(tokenState.accessToken);
        }

    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile])

    useEffect(() => {
        if (shouldTryCareCoordinatorProfile(userPracticeCoordinatorProfile) && tokenState?.accessToken) {
            requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);
        }
    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile]);

    useEffect(() => {
        if (tokenState?.accessToken && !PhysicianAdmitsStats.isLoaded && !PhysicianAdmitsStats.isLoading) {
            requestAdmitStatsForProvider(tokenState.accessToken);
        }

    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile]);


    if (!PhysicianAdmitsStats || PhysicianAdmitsStats.isLoading || !PhysicianAdmitsStats.isLoaded) {
        return (
            <Slide in timeout={1000} direction="right">
                <Grid container>
                    <Grid item md={4}>
                        <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled"></BarChartIcon>
                    </Grid>
                </Grid>
            </Slide>
        )
    }

    if (PhysicianAdmitsStats.isErrored) {
        return (<React.Fragment><p>Error Loading</p></React.Fragment>)
    }

    ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend, Filler
    );
    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "top" as const,
            },
            title: {
                display: true,
                text: "All Patients",
            },
        },
    };

    let width: number, height: number, gradient: number;
    function getBlueGradient(ctx: any, chartArea: any) {
        const chartWidth = chartArea.right - chartArea.left;
        const chartHeight = chartArea.bottom - chartArea.top;
        if (!gradient || width !== chartWidth || height !== chartHeight) {
            // Create the gradient because this is either the first render
            // or the size of the chart has changed
            width = chartWidth;
            height = chartHeight;
            gradient = ctx.createLinearGradient(
                0,
                chartArea.bottom,
                0,
                chartArea.top
            );

            gradient.addColorStop(0, "rgba(82, 189, 228, 0.05)");
            gradient.addColorStop(0.5, "rgba(82, 189, 228, 0.1)");
            gradient.addColorStop(1, "rgba(82, 189, 228, 0.2)");
        }

        return gradient;
    }

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];


    let labels = PhysicianAdmitsStats.physicianAdmits.map(val => `${monthNames[val.month - 1]}/${val.year}`);
    let values = PhysicianAdmitsStats.physicianAdmits.map(val => {
        return val.totalAdmits;
    });


    const data = {
        labels,
        datasets: [
            {
                label: "Admits",
                data: values,
                borderColor: "rgba(82, 189, 228, 1)",
                backgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) {
                        // This case happens on initial chart load
                        return;
                    }
                    return getBlueGradient(ctx, chartArea);
                },

                fill: true,
            },
        ],
    };
    const tableColumns = [{
        name: "Month"
    }, {
        name: "Physician"
    }, {
        name: "Admits"
    }]

    const gridoptions = {
        elevation: 0,
        filter: true,
        viewColumns: false,
        selectableRows: false,

    }

    return (
        <React.Fragment>
            <Grid xs={12} direction="column" container>
                <Grid item xs={12}>
                    <Line
                        options={options}
                        data={data}
                    />
                </Grid>

            </Grid>


        </React.Fragment>
    );
};

export default connect(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(AcutePatientsByMonth as any);
